import { CheckoutContext } from '@/context/CheckoutContext';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { RedirectPaymentManager } from '@/services/redirectPaymentManager';
import { notifyPaymentIntentFailed } from '@/utils/notifier';
import { AppchargeErrorType, RedirectPaymentCancelReason } from '@/types/payment';
import { PaymentMethodEnum } from '@/types/common';
import { canCancelRedirectPaymentManually } from '@/utils/redirectPayment';
import { isMobileDevice } from '@/utils/device';
import { stripeInPageNextActionPaymentMethods } from '@/constants/stripe';
import { CashAppRedirect } from './CashAppRedirect';
import { useGetStatus } from '@/api/status';
import { useTranslate } from '@/hooks/useTranslate';
import { ErrorLogs, InfoLogs } from '@/constants/logs';
import { logError, logInfo } from '@/utils/logger';
import { getAppchargeErrorMessage, getRawPaymentFailureMessage } from '@/utils/error';
import { useCheckoutData } from '@/api/useCheckoutData';
import { PaymentProcessOverlay } from '../Common/PaymentProcessOverlay';

export const RedirectPayment = () => {
  const context = useContext(CheckoutContext);
  const checkoutData = useCheckoutData({ enableFetch: false });
  const {
    redirectPayment,
    actions: {
      setIsInPaymentProcess,
      setRedirectPayment,
      setSuccessScreen,
      setError,
      startOrderPolling,
      closeRedirectTab,
    },
    paymentMethodSelected,
    otherMethodSelected,
    paymentMethodsSettings,
  } = context;
  const { redirectUrl, provider, clientSecret, handlePaymentSuccess, handlePaymentError, openedTab, orderId } =
    redirectPayment;
  const selectedPaymentMethod = useMemo(
    () => (paymentMethodSelected === PaymentMethodEnum.OTHER ? otherMethodSelected : paymentMethodSelected),
    [paymentMethodSelected, otherMethodSelected]
  );
  const paymentMethodId = useMemo(
    () =>
      paymentMethodsSettings.find(
        (method) => method.paymentMethodName === selectedPaymentMethod && method.provider === provider
      )?.id ?? '',
    [paymentMethodsSettings, selectedPaymentMethod, provider]
  );
  const { mutateAsync: getStatus } = useGetStatus();
  const managerRef = useRef<RedirectPaymentManager | null>(null);
  const tabIsOpenedRef = useRef(false);
  // First render of this component is the moment the redirect overlay goes up.
  const overlayShownAtRef = useRef(Date.now());
  // Set when the intent hits `processing` and order polling takes over. Ref for
  // a sync cancel guard (click can race the re-render); state to pull the
  // button. Overlay stays up on purpose — Afterpay isn't in the polling-loader
  // list — but cancel must not survive past authorization.
  const fulfillmentStartedRef = useRef(false);
  const [fulfillmentStarted, setFulfillmentStarted] = useState(false);
  const [isProcessing] = useState(true);
  const { t } = useTranslate();

  const usesStripeInPageNextActionOnDesktop = useMemo(
    () =>
      !!selectedPaymentMethod &&
      stripeInPageNextActionPaymentMethods.includes(selectedPaymentMethod) &&
      !isMobileDevice(),
    [selectedPaymentMethod]
  );

  const canCancelManually = useMemo(
    () => canCancelRedirectPaymentManually(provider, selectedPaymentMethod),
    [provider, selectedPaymentMethod]
  );

  // Deliberately NOT the failed-payment path: nothing was declined, the shopper
  // walked away from the provider's page. So no `notifyPaymentIntentFailed` and
  // no `handlePaymentError` — those tell the publisher's SDK the attempt failed
  // and surface a decline message the shopper hasn't earned. This only puts the
  // checkout back where it was before the redirect, ready for another attempt.
  const handleShopperCancel = useCallback(() => {
    // Authorization already handed off to order polling — cancel would only
    // clear local payment state while fulfillment keeps running.
    if (fulfillmentStartedRef.current) return;

    // `waitedMs` is the whole point of this log: it says how long shoppers sit on
    // the overlay before giving up, which is what tells us whether they're
    // abandoning or just outlasting a slow PSP page.
    logInfo(InfoLogs.checkoutPaymentCancelled, {
      reason: RedirectPaymentCancelReason.SHOPPER_CANCELLED,
      provider,
      paymentMethod: selectedPaymentMethod,
      orderId,
      waitedMs: Date.now() - overlayShownAtRef.current,
      redirectUrl,
    });
    managerRef.current?.cleanup();
    setIsInPaymentProcess(false);
    // Best-effort close of the tab the shopper left behind; also clears
    // CheckoutContent's pre-opened tab ref so retrying opens a fresh tab
    // instead of reusing the dead handle.
    closeRedirectTab();
    setRedirectPayment({});
  }, [
    provider,
    selectedPaymentMethod,
    orderId,
    redirectUrl,
    setIsInPaymentProcess,
    closeRedirectTab,
    setRedirectPayment,
  ]);

  // Mount-scoped on purpose: the polling effect below re-runs whenever its deps
  // change, and tearing the manager down there would kill a live session (the
  // `tabIsOpenedRef` guard prevents it from ever restarting). Only a real
  // unmount stops the loop and closes the tab — without this the loop outlived
  // the component, which now matters because polling is no longer time-bounded.
  useEffect(() => () => managerRef.current?.cleanup(), []);

  useEffect(() => {
    if (!redirectUrl || !provider || tabIsOpenedRef.current || usesStripeInPageNextActionOnDesktop) return;

    logInfo(InfoLogs.checkoutPaymentRedirected, {
      provider,
      redirectUrl,
    });
    const manager = new RedirectPaymentManager(provider, selectedPaymentMethod);
    managerRef.current = manager;
    if (openedTab) {
      manager.setTab(openedTab);
      manager.sendRedirectMessage(redirectUrl);
    } else {
      manager.openTab(redirectUrl);
    }
    tabIsOpenedRef.current = true;

    if (clientSecret || paymentMethodId) {
      manager.startPolling(
        clientSecret ?? '',
        (params: Record<string, unknown>) => {
          const intent = (params as { paymentIntent?: Record<string, unknown> }).paymentIntent;
          logInfo(InfoLogs.checkoutPaymentSuccess, {
            params: intent ? { ...params, paymentIntent: { ...intent, client_secret: undefined } } : params,
            redirectUrl,
          });
          setRedirectPayment({});
          setSuccessScreen({ isOpen: true, paymentDetails: { params }, provider });
          handlePaymentSuccess?.();
        },
        (params: Record<string, unknown>) => {
          const intent = (params as { paymentIntent?: Record<string, unknown> }).paymentIntent;
          logInfo(InfoLogs.checkoutPaymentProcessed, {
            params: intent ? { ...params, paymentIntent: { ...intent, client_secret: undefined } } : params,
            redirectUrl,
          });
          // Intent authorized — revoke cancel before opening success / polling so
          // a late click cannot reset checkout under a still-completing order.
          fulfillmentStartedRef.current = true;
          setFulfillmentStarted(true);
          setSuccessScreen({ isOpen: true, paymentDetails: { params }, provider });
          if (orderId) {
            startOrderPolling(orderId);
          }
        },
        (_, params: Record<string, unknown>) => {
          const appChargeErrorType = params?.appChargeErrorType as AppchargeErrorType | undefined;
          // Context belongs in `additionalData` (3rd arg): a plain object in
          // the `Error` slot reaches RUM as "[object Object]" with its fields
          // dropped, which is what made redirect declines undiagnosable.
          logError(ErrorLogs.checkoutPaymentError, undefined, {
            appChargeErrorType: appChargeErrorType ?? 'none',
            rawReason: getRawPaymentFailureMessage(params) ?? 'none',
            paymentMethod: selectedPaymentMethod,
            redirectUrl,
          });
          notifyPaymentIntentFailed(checkoutData.data!, selectedPaymentMethod, {
            reason: getRawPaymentFailureMessage(params),
          });
          setError(getAppchargeErrorMessage(t, appChargeErrorType));
          setIsInPaymentProcess(false);
          setRedirectPayment({});
          handlePaymentError?.();
        },
        // Same reasoning as `handleShopperCancel` above: the shopper closed the
        // tab without a terminal PSP status, nothing was declined. No
        // `notifyPaymentIntentFailed` and no `handlePaymentError` here either —
        // this only resets the checkout to its pre-redirect state.
        (params: Record<string, unknown>) => {
          const intent = (params as { paymentIntent?: Record<string, unknown> }).paymentIntent;
          logInfo(InfoLogs.checkoutPaymentCancelled, {
            params: intent ? { ...params, paymentIntent: { ...intent, client_secret: undefined } } : params,
            reason: params.reason ?? RedirectPaymentCancelReason.TAB_CLOSED,
            redirectUrl,
          });
          setIsInPaymentProcess(false);
          setRedirectPayment({});
        },
        async (redirectResult: string) => await getStatus({ paymentMethodId, redirectResult }),
        paymentMethodId
      );
    }
  }, [
    redirectUrl,
    provider,
    clientSecret,
    orderId,
    setRedirectPayment,
    setIsInPaymentProcess,
    setSuccessScreen,
    setError,
    startOrderPolling,
    handlePaymentSuccess,
    handlePaymentError,
    selectedPaymentMethod,
    paymentMethodId,
    getStatus,
  ]);

  if (usesStripeInPageNextActionOnDesktop || selectedPaymentMethod === PaymentMethodEnum.PIX) {
    return <CashAppRedirect />;
  }
  if (!isProcessing) return null;

  return (
    <PaymentProcessOverlay
      onCancel={canCancelManually && !fulfillmentStarted ? handleShopperCancel : undefined}
    />
  );
};
